// src/pages/Orders/invoicePdf.js
// Builds the invoice PDF (same layout as InvoiceDetail "Download") and returns it as base64.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const formatDate = (s) => (s ? String(s).split('T')[0] : 'N/A');

export const getInvoiceNumber = (order) => `INV0${order.order_Id}`;

// /order/orderdProducts/<id> response -> simple product rows
export const transformOrderedProducts = (prodData) =>
  (Array.isArray(prodData) ? prodData : []).map((item) => {
    const base = item.product_Id || {};
    let price = 0;
    if (item.price_per_unit !== undefined && item.price_per_unit !== null) price = parseFloat(item.price_per_unit);
    else if (item.custom_price !== undefined && item.custom_price !== null) price = parseFloat(item.custom_price);
    else price = parseFloat(base.product_Price || 0);

    return {
      id: item.id,
      name: base.product_Name || 'Unnamed Product',
      price: Number.isFinite(price) ? price : 0,
      qty: item.quantity || 0,
      sku: base.SKU || '',
      productNo: base.product_Id || '',
    };
  });

// Logo is loaded only once for the whole bulk run
let logoPromise = null;
export const getLogoBase64 = () => {
  if (!logoPromise) {
    logoPromise = (async () => {
      try {
        const res = await fetch('/Hira-Tech-Logo.png');
        if (!res.ok) throw new Error('logo fetch failed');
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
      } catch (e) {
        return null;
      }
    })();
  }
  return logoPromise;
};

/**
 * @returns {string} base64 PDF (WITHOUT the "data:application/pdf;base64," prefix)
 */
export const buildInvoicePdfBase64 = ({ order, products, company, logo, formatPrice }) => {
  const pdfMoney = (n) => String(formatPrice(n)).replace(/[^\x20-\x7E]/g, '').trim();

  // ---- money ----
  const itemsTotal = products.reduce((s, p) => s + toNum(p.price) * toNum(p.qty), 0);
  const subtotal = itemsTotal > 0 ? itemsTotal : (parseFloat(order.subtotal) || 0);
  const discount = parseFloat(order.discount) || 0;
  const delivery = parseFloat(order.delivery_Charges) || 0;
  const vat = parseFloat(order.vat_amount) || 0;
  const total =
    order.total_Amount !== undefined && order.total_Amount !== null
      ? parseFloat(order.total_Amount) || 0
      : subtotal + delivery + vat - discount;

  const extraTotals = [
    delivery > 0 ? { label: 'Delivery Charges', value: delivery, sign: '' } : null,
    vat > 0 ? { label: 'VAT', value: vat, sign: '' } : null,
    discount > 0 ? { label: 'Discount', value: discount, sign: '-' } : null,
  ].filter(Boolean);
  const showGrandTotal = extraTotals.length > 0;

  // ---- texts ----
  const invoiceNumber = getInvoiceNumber(order);
  const customerName = order.customer
    ? `${order.customer.first_Name || ''} ${order.customer.last_Name || ''}`.trim() || 'N/A'
    : 'N/A';
  const customerPhone = order.customer ? `${order.customer.phone_Number || ''}` : '';
  const customerEmail = order.customer?.email || '';
  const addressString = order.address
    ? `${order.address.house_Number ? order.address.house_Number + ' ' : ''}${order.address.street_Number ? order.address.street_Number + ' ' : ''}${order.address.area || ''} ${order.address.city || ''}`.trim()
    : 'N/A';

  const companyName = company?.company_name || 'Hira Tech International Solutions';
  const companyPhone = company?.phone || '';
  const companyEmail = company?.email || '';
  const companyAddress1 = company?.address || 'House # 13, Street # 14';
  const companyAddress2 = company?.address_line_2 || 'Sector C1 - Bahria Enclave, Islamabad';

  const bankRows = [
    ['Bank Name', company?.bank_name],
    ['Account Title', company?.account_title],
    ['Account Number', company?.account_number],
    ['Branch', company?.branch || company?.bank_branch],
    ['Sort Code', company?.sort_code],
    ['IBAN', company?.iban],
    ['BIC', company?.bic],
  ].filter(([, v]) => v && String(v).trim());

  const agentName = order.agent_Name || '';
  const agentPhone = order.agent_number || '';
  const courierService = order.courier_service || '';
  const orderNote = order.note || '';

  // ---- PDF ----
  const doc = new jsPDF('p', 'mm', 'a4');

  if (logo) {
    try { doc.addImage(logo, 'PNG', 12, 10, 50, 18); } catch (e) { /* skip logo */ }
  }

  const headerX = 12;
  let y = 35;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(companyName, headerX, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  if (companyAddress1) { doc.text(companyAddress1, headerX, y); y += 4; }
  if (companyAddress2) { doc.text(companyAddress2, headerX, y); y += 4; }
  if (companyPhone) { doc.text(`Phone: ${companyPhone}`, headerX, y); y += 4; }

  y += 10;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Ship-To-Party', headerX, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(customerName, headerX, y);
  y += 4;
  doc.splitTextToSize(addressString, 80).forEach((line) => { doc.text(line, headerX, y); y += 4; });
  if (customerPhone) { doc.text(`Contact: ${customerPhone}`, headerX, y); y += 4; }
  if (courierService) { doc.text(`Courier Service: ${courierService}`, headerX, y); y += 4; }

  // Invoice box (right)
  const boxX = 120, boxY = 35, boxWidth = 75, boxHeight = 68;
  doc.setDrawColor(0, 74, 173);
  doc.rect(boxX, boxY, boxWidth, boxHeight);
  doc.setFillColor(0, 74, 173);
  doc.rect(boxX, boxY, boxWidth, 9, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Invoice', boxX + 4, boxY + 6);
  doc.text(invoiceNumber, boxX + boxWidth - 4, boxY + 6, { align: 'right' });

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  let iy = boxY + 15;
  doc.text(`Date: ${formatDate(order.order_Placment_Date)}`, boxX + 4, iy); iy += 5;
  doc.text('Page: 1 of 1', boxX + 4, iy); iy += 5;
  doc.text(`Order ID: ${order.order_Id || ''}`, boxX + 4, iy); iy += 5;
  doc.text(`Tracking ID: ${order.order_Tracking_Id || ''}`, boxX + 4, iy); iy += 5;
  if (courierService) { doc.text(`Courier: ${courierService}`, boxX + 4, iy); iy += 5; }
  if (customerEmail) { doc.text(`Contact: ${customerEmail}`, boxX + 4, iy); iy += 5; }
  doc.text(`Customer Ref: ${order.customer_Reference || 'N/A'}`, boxX + 4, iy); iy += 5;

  doc.setDrawColor(0, 74, 173);
  doc.line(boxX, iy, boxX + boxWidth, iy);
  iy += 5;
  if (agentName) { doc.text(`Agent: ${agentName}`, boxX + 4, iy); iy += 5; }
  if (agentPhone) { doc.text(`Mobile: ${agentPhone}`, boxX + 4, iy); }

  // Products table
  const tableData = products.map((item) => [
    item.productNo || item.sku || 'N/A',
    item.name || 'N/A',
    item.qty || 'N/A',
    pdfMoney(toNum(item.price)),
    pdfMoney(toNum(item.price) * toNum(item.qty)),
  ]);

  const footRows = [
    ['', '', '', 'Net Value:', pdfMoney(subtotal)],
    ...extraTotals.map((t) => ['', '', '', `${t.label}:`, `${t.sign}${pdfMoney(t.value)}`]),
    ...(showGrandTotal ? [['', '', '', 'Total Amount:', pdfMoney(total)]] : []),
  ];

  autoTable(doc, {
    startY: 110,
    head: [['Item', 'Material/Description', 'Quantity', 'Price/SU', 'Value']],
    body: tableData,
    foot: footRows,
    showFoot: 'lastPage',
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3, lineColor: [140, 140, 140], lineWidth: 0.2 },
    headStyles: { fillColor: '#004aad', textColor: 255, halign: 'left' },
    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 22, halign: 'center' },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 28, halign: 'right' },
    },
    didParseCell: (data) => {
      if (data.section === 'head' && data.column.index >= 2) {
        data.cell.styles.halign = data.column.index === 2 ? 'center' : 'right';
      }
      if (data.section === 'foot') {
        data.cell.styles.lineWidth = 0;
        data.cell.styles.fillColor = [255, 255, 255];
        const isNet = data.row.index === 0;
        const isGrand = showGrandTotal && data.row.index === footRows.length - 1;
        if (!isNet && !isGrand) data.cell.styles.fontStyle = 'normal';
      }
    },
  });

  let currentY = (doc.lastAutoTable.finalY || 110) + 8;

  if (orderNote) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Note:', 12, currentY);
    doc.setFont('helvetica', 'normal');
    const splitNote = doc.splitTextToSize(orderNote, 180);
    doc.text(splitNote, 12, currentY + 5);
    currentY += splitNote.length * 5 + 10;
  }

  if (currentY > 235) { doc.addPage(); currentY = 20; }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Terms of payment: 30 days end-of-month', 12, currentY);
  currentY += 10;

  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(12, currentY, 198, currentY);
  currentY += 6;

  if (companyEmail) doc.text(`E-Mail: ${companyEmail}`, 105, currentY, { align: 'center' });

  currentY += 8;
  if (bankRows.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.text('Bank Details:', 12, currentY);
    doc.setFont('helvetica', 'normal');
    currentY += 4;
    bankRows.forEach(([label, value]) => { doc.text(`${label}: ${value}`, 12, currentY); currentY += 4; });
  }

  return doc.output('datauristring').split(',')[1];
};